// Angular
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { isIndexableDomain } from '../../../_constants/domain-config';
// Lodash
// CRUD

@Injectable({
  providedIn: 'root'
})
export class MetaService {
  private robotsObserver: MutationObserver;

  constructor(@Inject(DOCUMENT) private dom) {
  }

  /**
   * Enforce the domain indexing allowlist for the lifetime of the application.
   * Individual pages may still opt into noindex, but an unapproved domain can
   * never overwrite the global policy with index/follow during SPA navigation.
   */
  startRobotsPolicyEnforcement(): void {
    if (this.dom.defaultView) {
      this.dom.defaultView.__robotsPolicyStarted = true;
    }

    if (isIndexableDomain()) {
      const robots: HTMLMetaElement = this.dom.head.querySelector('meta[name="robots"]');
      if (robots && robots.getAttribute('content') === 'noindex, nofollow') {
        robots.setAttribute('content', 'index, follow');
      }
      return;
    }

    this.enforceRobotsPolicy();

    if (this.robotsObserver || !this.dom.defaultView?.MutationObserver) {
      return;
    }

    this.robotsObserver = new this.dom.defaultView.MutationObserver(() => {
      this.enforceRobotsPolicy();
    });
    this.robotsObserver.observe(this.dom.head, {
      attributes: true,
      attributeFilter: ['content'],
      childList: true,
      subtree: true,
    });
  }

  stopRobotsPolicyEnforcement(): void {
    if (this.dom.defaultView) {
      this.dom.defaultView.__robotsPolicyStarted = false;
    }
    if (this.robotsObserver) {
      this.robotsObserver.disconnect();
      this.robotsObserver = undefined;
    }
  }

  private enforceRobotsPolicy(): void {
    if (isIndexableDomain()) {
      return;
    }

    let robotsTags: NodeListOf<HTMLMetaElement> = this.dom.head.querySelectorAll('meta[name="robots"]');
    if (!robotsTags.length) {
      const robots = this.dom.createElement('meta');
      robots.setAttribute('name', 'robots');
      this.dom.head.appendChild(robots);
      robotsTags = this.dom.head.querySelectorAll('meta[name="robots"]');
    }

    robotsTags.forEach(robots => {
      if (robots.getAttribute('content') !== 'noindex, nofollow') {
        robots.setAttribute('content', 'noindex, nofollow');
      }
    });
  }

  /**
   * Set one page-level robots directive while retaining the global domain
   * allowlist. Pages on an unapproved host can never opt themselves in.
   */
  setRobots(content: 'index, follow' | 'noindex, follow' | 'noindex, nofollow'): void {
    const desiredContent = isIndexableDomain() ? content : 'noindex, nofollow';
    let robotsTags: NodeListOf<HTMLMetaElement> = this.dom.head.querySelectorAll('meta[name="robots"]');

    if (!robotsTags.length) {
      const robots = this.dom.createElement('meta');
      robots.setAttribute('name', 'robots');
      this.dom.head.appendChild(robots);
      robotsTags = this.dom.head.querySelectorAll('meta[name="robots"]');
    }

    robotsTags.forEach((robots, index) => {
      if (index === 0) {
        robots.setAttribute('content', desiredContent);
      } else {
        robots.parentNode.removeChild(robots);
      }
    });
  }

  createCanonicalURL(url?: string) {
    const canURL = url == undefined ? this.dom.URL : url;
    // Reuse an existing canonical tag instead of appending a new one on every
    // navigation — otherwise SPA route changes leave multiple <link rel="canonical">.
    let link: HTMLLinkElement = this.dom.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.dom.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.dom.head.appendChild(link);
    }
    link.setAttribute('href', canURL);
  }

  /**
   * Insert or update a JSON-LD structured-data block, keyed by `id` so repeated
   * calls (e.g. across SPA navigation) replace rather than duplicate it.
   */
  setJsonLd(id: string, data: any) {
    let script: HTMLScriptElement = this.dom.head.querySelector(`script[type="application/ld+json"]#${id}`);
    if (!script) {
      script = this.dom.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('id', id);
      this.dom.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  }

  /** Remove a previously-inserted JSON-LD block by id, if present. */
  removeJsonLd(id: string) {
    const script = this.dom.head.querySelector(`script[type="application/ld+json"]#${id}`);
    if (script) {
      script.parentNode.removeChild(script);
    }
  }
}
